import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { usePerioExam } from '@/hooks/usePerioExams';
import type { PerioMeasurement, PerioSite } from '@/utils/perio';
import { PERIO_SITES } from '@/utils/perio';
import { Button } from '@/components/ui/Button';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PerioComparisonViewProps {
  examIdA: string;
  examIdB: string;
  labelA: string;
  labelB: string;
  onClose: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildMeasurementMap(
  measurements: PerioMeasurement[],
): Map<string, PerioMeasurement> {
  const map = new Map<string, PerioMeasurement>();
  for (const m of measurements) {
    map.set(`${m.tooth_fdi}:${m.site}`, m);
  }
  return map;
}

type DiffResult = 'improved' | 'worsened' | 'unchanged' | 'no-data';

function diffPD(
  older: PerioMeasurement | undefined,
  newer: PerioMeasurement | undefined,
): DiffResult {
  if (!older || !newer) return 'no-data';
  const oPD = older.pocket_depth_mm ?? 0;
  const nPD = newer.pocket_depth_mm ?? 0;
  if (nPD < oPD) return 'improved';
  if (nPD > oPD) return 'worsened';
  return 'unchanged';
}

function diffColor(diff: DiffResult): string {
  switch (diff) {
    case 'improved': return 'bg-ds-green-tint text-ds-green-ink';
    case 'worsened': return 'bg-ds-red-tint text-ds-red-ink';
    case 'unchanged': return 'bg-ds-neutral-tint text-ds-text bg-ds-card text-ds-muted';
    default: return 'bg-ds-sunk text-ds-muted bg-ds-card';
  }
}

// Unique FDI teeth across both exams
function collectTeeth(
  a: PerioMeasurement[],
  b: PerioMeasurement[],
): string[] {
  const set = new Set<string>();
  for (const m of [...a, ...b]) set.add(m.tooth_fdi);
  return Array.from(set).sort();
}

// ── Sub-component: comparison column ─────────────────────────────────────────

function ComparisonCell({
  value,
  diff,
}: {
  value: number | null | undefined;
  diff: DiffResult;
}) {
  return (
    <td className={`border border-ds-line px-1 py-0.5 text-center text-xs font-medium border-ds-line ${diffColor(diff)}`}>
      {value ?? '–'}
    </td>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioComparisonView({
  examIdA,
  examIdB,
  labelA,
  labelB,
  onClose,
}: PerioComparisonViewProps) {
  const { t } = useTranslation();
  const { data: measurementsA = [], isLoading: loadA } = usePerioExam(examIdA);
  const { data: measurementsB = [], isLoading: loadB } = usePerioExam(examIdB);

  const mapA = buildMeasurementMap(measurementsA);
  const mapB = buildMeasurementMap(measurementsB);

  const teeth = collectTeeth(measurementsA, measurementsB);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-ds-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ds-line px-6 py-4">
          <h2 className="text-lg font-semibold text-ds-text">
            {t('dental.perio.compare.title')}
          </h2>
          <Button type="button" variant="tertiary" size="icon" onClick={onClose} aria-label={t('common.close')}>
            <X />
          </Button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 border-b border-ds-line px-6 py-2">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-ds-green-tint" />
            <span className="text-xs text-ds-muted">{t('dental.perio.compare.improved')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-ds-red-tint" />
            <span className="text-xs text-ds-muted">{t('dental.perio.compare.worsened')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-ds-neutral-tint" />
            <span className="text-xs text-ds-muted">{t('dental.perio.compare.unchanged')}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {(loadA || loadB) && (
            <p className="text-sm text-ds-muted">Indlæser...</p>
          )}

          {!loadA && !loadB && (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="border border-ds-line px-2 py-1 text-left">Tand</th>
                  <th className="border border-ds-line px-2 py-1 text-left">Site</th>
                  {PERIO_SITES.map(site => (
                    <th
                      key={`a-${site}`}
                      className="border border-ds-line px-2 py-1 text-center"
                    >
                      {labelA}<br />{site}
                    </th>
                  ))}
                  {PERIO_SITES.map(site => (
                    <th
                      key={`b-${site}`}
                      className="border border-ds-line px-2 py-1 text-center"
                    >
                      {labelB}<br />{site}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teeth.map(fdi =>
                  PERIO_SITES.map(site => {
                    const key = `${fdi}:${site}` as string;
                    const mA = mapA.get(key);
                    const mB = mapB.get(key);
                    const diff = diffPD(mA, mB);

                    return (
                      <tr key={key}>
                        {site === 'MB' && (
                          <td
                            rowSpan={6}
                            className="border border-ds-line px-2 py-1 text-center font-bold text-ds-text text-ds-muted"
                          >
                            {fdi}
                          </td>
                        )}
                        <td className="border border-ds-line px-2 py-1 text-ds-muted">
                          {site}
                        </td>
                        <ComparisonCell value={mA?.pocket_depth_mm} diff={diff} />
                        <ComparisonCell value={mB?.pocket_depth_mm} diff={diff} />
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Suppress TS warning for unused site variable in map
void (PERIO_SITES as PerioSite[]);
